import { CheckCircle, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Modal, ModalFooter } from "@/components/ui";
import type { ReceiptAnalyzeResponse } from "@/types";

interface ReceiptConfirmModalProps {
  analyzeResult: ReceiptAnalyzeResponse;
  confirmingReceipt: boolean;
  onConfirm: () => void;
  onRetake: () => void;
}

export function ReceiptConfirmModal({
  analyzeResult,
  confirmingReceipt,
  onConfirm,
  onRetake,
}: ReceiptConfirmModalProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("components");

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen
      onClose={onRetake}
      title={t("receiptModalTitle")}
      description={t("receiptModalSubtitle")}
      size="sm"
      role="alertdialog"
      dismissOnBackdrop={false}
      showCloseButton={false}
      footer={
        <ModalFooter>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={confirmingReceipt}
            leftIcon={<CheckCircle className="h-4 w-4" />}
          >
            {confirmingReceipt
              ? t("receiptModalConfirming")
              : t("receiptModalYes")}
          </Button>
          <Button
            variant="outline"
            onClick={onRetake}
            disabled={confirmingReceipt}
            leftIcon={<XCircle className="h-4 w-4" />}
          >
            {t("receiptModalRetake")}
          </Button>
        </ModalFooter>
      }
    >
      <div className="rounded-xl bg-surface-50 p-4">
        <p className="text-sm text-surface-600">{t("receiptModalExtracted")}</p>
        <p className="mt-1 text-3xl font-bold text-surface-900">
          {analyzeResult.extracted_total_display}
        </p>
        <p className="mt-1 text-sm text-surface-500">
          {t("receiptModalConfirmHint")}
        </p>
      </div>

      {analyzeResult.fraud_warning ? (
        <Alert variant="error" className="mt-4">
          {analyzeResult.fraud_warning}
        </Alert>
      ) : null}

      <Alert variant="warning" className="mt-4">
        {t("receiptModalMismatch")}
      </Alert>
    </Modal>
  );
}
